import * as React from 'react'
import ReactSelect from 'react-select'
import styled from 'styled-components'
import I18n from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { agentService, paymentDetailService, utilService } from '../../../../core/services'
import {
  AvatarImage,
  EditTableColumn,
  Pagination,
  Panel,
  PanelTitle,
  Select,
  StatusBadge,
} from '../../../atoms'
import { EditTable } from '../../../organisms'
import { AgentRankTag } from '../../../molecules/Weekly/AgentRankTag'
import SidebarLayout from '../../_layouts/sidebarLayout'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'

declare var window: interfaces.IWindow

interface IFilters {
  guide_id: string | null
  status: string | null
  start_month: string | null
  end_month: string | null
}

interface IPaginationMeta {
  current_page: number
  total_pages: number
  next_page: number
  prev_page: number
  total_count: number
}

interface IProps {
  payment_details: IJsonResponse
  selectable_guides: IJsonResponse
  user: IJsonResponse
  pagination: IPaginationMeta
  current_filters: IFilters
  sheet_export_enabled: boolean
}

const attrT = (k: string) => I18n.t(k, { scope: 'activerecord.attributes.payment_detail' })

const navigateWithParam = (key: string, value: string) => {
  const p = new URLSearchParams(window.location.search)
  if (value) {
    p.set(key, value)
  } else {
    p.delete(key)
  }
  p.delete('page')
  location.href = `/agent/payment_details?${p.toString()}`
}

const navigateToPage = (page: number) => {
  const p = new URLSearchParams(window.location.search)
  p.set('page', String(page))
  location.href = `/agent/payment_details?${p.toString()}`
}

// 現在の検索フィルタを引き継いだCSV出力URL（ページングは除外し全件出力）
const csvExportHref = () => {
  const p = new URLSearchParams(window.location.search)
  p.delete('page')
  const qs = p.toString()
  return `/agent/payment_details.csv${qs ? `?${qs}` : ''}`
}

// guide_report の progress に応じた StatusBadge のタイプ
const guideReportBadgeType = (
  progress?: string
): 'success' | 'caution' | 'usually' | 'info' | 'empty' | 'rejected' => {
  switch (progress) {
    case 'all_check_done':
    case 'auto_check_done':
      return 'success'
    case 'first_check_done':
      return 'usually'
    case 'confirming':
      return 'caution'
    case 'rejected':
      return 'rejected'
    case 'created':
      return 'info'
    case 'not_yet':
    default:
      return 'empty'
  }
}

const COLUMNS = [
  {
    name: I18n.t('generic.tour_date'),
    field: 'tour_day',
    renderElement: (r: any) => {
      const day = r.tour?.day
      const startTime = r.tour?.start_time
      if (!day) {
        return <EditTableColumn>-</EditTableColumn>
      }
      return (
        <EditTableColumn>
          {day}
          {startTime ? ` ${startTime}` : ''}
        </EditTableColumn>
      )
    },
  },
  {
    name: I18n.t('post.post_name'),
    field: 'post_name',
    renderElement: (r: any) => <EditTableColumn>{r.tour?.post?.short_name || '-'}</EditTableColumn>,
  },
  {
    name: I18n.t('generic.guide'),
    field: 'guide',
    renderElement: (r: any) => {
      const guide = r.tour_guide?.guide
      if (!guide) {
        return <EditTableColumn>-</EditTableColumn>
      }
      return (
        <EditTableColumn>
          <GuideCell>
            <a href={`/agent/guides/${guide.id}/edit`} className="avatar">
              <AvatarImage size={32} avatarURL={guide.avatar_url} />
            </a>
            <div className="meta">
              <a href={`/agent/guides/${guide.id}/edit`} className="name">
                {`${guide.fullname || '-'} (${guide.username || '-'})`}
              </a>
              {guide.agent_rank_as_guide && (
                <div className="rank">
                  <AgentRankTag agentRank={guide.agent_rank_as_guide} />
                </div>
              )}
            </div>
          </GuideCell>
        </EditTableColumn>
      )
    },
  },
  {
    name: attrT('total'),
    field: 'total',
    renderElement: (r: any) => (
      <EditTableColumn type="bold">{utilService.formatPrice(r.total)}</EditTableColumn>
    ),
  },
  {
    name: attrT('guide_fee'),
    field: 'guide_fee',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.guide_fee)}</EditTableColumn>
    ),
  },
  {
    name: attrT('special_activity_fee'),
    field: 'calculated_special_activity_fee',
    renderElement: (r: any) => (
      <EditTableColumn>
        {utilService.formatPrice(r.calculated_special_activity_fee)}
      </EditTableColumn>
    ),
  },
  {
    name: attrT('large_group_fee'),
    field: 'calculated_large_group_fee',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.calculated_large_group_fee)}</EditTableColumn>
    ),
  },
  {
    name: attrT('other_fee'),
    field: 'other_fee',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.other_fee)}</EditTableColumn>
    ),
  },
  {
    name: attrT('travel_cost'),
    field: 'travel_cost',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.travel_cost)}</EditTableColumn>
    ),
  },
  {
    name: attrT('advance_cost'),
    field: 'calculated_advance_cost',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.calculated_advance_cost)}</EditTableColumn>
    ),
  },
  {
    name: attrT('deduction'),
    field: 'deduction',
    renderElement: (r: any) => (
      <EditTableColumn>−{utilService.formatPrice(r.deduction)}</EditTableColumn>
    ),
  },
  {
    name: attrT('cancellation_fee'),
    field: 'cancellation_fee',
    renderElement: (r: any) => (
      <EditTableColumn>{utilService.formatPrice(r.cancellation_fee)}</EditTableColumn>
    ),
  },
  {
    name: I18n.t('generic.status'),
    field: 'status',
    renderElement: (r: any) => <EditTableColumn>{r.status_i18n}</EditTableColumn>,
  },
]

const Index: React.FC<IProps> = props => {
  const { data: rows } = agentService.getDataFromJson(props.payment_details)
  const { data: guides } = agentService.getDataFromJson(props.selectable_guides)
  const { data: userData } = agentService.getDataFromJson(props.user)
  const f = props.current_filters
  // スプレッドシート出力の二重リクエスト防止
  const [sheetExportRequested, setSheetExportRequested] = React.useState(false)

  const exportSheet = async () => {
    setSheetExportRequested(true)
    try {
      const { flush } = await paymentDetailService.export()
      window.flashMessages.addMessage({ text: flush.message, type: flush.type })
    } catch {
      // エラー表示は httpClient の interceptor が行う。失敗時のみ再実行できるようにする
      setSheetExportRequested(false)
    }
  }

  // Operation 列 (Tour Group ボタン + Report ボタン・ステータス)
  const renderRecordControls = (r: any) => {
    const tourLink = r.tour_group_id
      ? `/agent/tours/weekly?start=${r.tour?.day}&tour_id=${r.tour?.id}`
      : null
    const guideReport = r.guide_report
    const reportLink = guideReport ? `/agent/guide_reports/${guideReport.id}/edit` : null

    if (!tourLink && !reportLink) {
      return null
    }

    return (
      <RecordControls>
        {tourLink && (
          <a className="opButton opButton--tour" href={tourLink}>
            {I18n.t('generic.tour_group')}
          </a>
        )}
        {reportLink && (
          <div className="reportBlock">
            <a className="opButton opButton--report" href={reportLink}>
              {I18n.t('generic.report')}
            </a>
            <StatusBadge
              label={guideReport.progress_i18n}
              type={guideReportBadgeType(guideReport.progress)}
            />
          </div>
        )}
      </RecordControls>
    )
  }

  // typeahead 用の guide options
  const guideOptions = React.useMemo(
    () =>
      guides.map((g: any) => ({
        value: String(g.id),
        label: `${g.fullname || g.username || '-'} (${g.username || '-'})`,
      })),
    [guides]
  )
  const selectedGuide = React.useMemo(
    () => guideOptions.find(o => o.value === f.guide_id) || null,
    [guideOptions, f.guide_id]
  )

  return (
    <SidebarLayout user={userData}>
      <Panel title={<PanelTitle title={I18n.t('payment_details.index_title')} />}>
        <SearchConditions>
          <div className="conditionItem">
            <label className="conditionLabel" htmlFor="filter-guide-id">
              {I18n.t('generic.guide')}
            </label>
            <div className="conditionControl typeahead">
              <ReactSelect
                inputId="filter-guide-id"
                aria-label={I18n.t('generic.guide')}
                isClearable={true}
                placeholder=""
                options={guideOptions}
                value={selectedGuide}
                onChange={(opt: any) => navigateWithParam('guide_id', opt ? opt.value : '')}
              />
            </div>
          </div>
          <div className="conditionItem">
            <label className="conditionLabel" htmlFor="status">
              {I18n.t('generic.status')}
            </label>
            <div className="conditionControl">
              <Select
                name="status"
                defaultValue={f.status || ''}
                options={[
                  { value: '', label: '' },
                  {
                    value: 'ongoing',
                    label: I18n.t('activerecord.attributes.payment_detail.statuses.ongoing'),
                  },
                  {
                    value: 'canceled',
                    label: I18n.t('activerecord.attributes.payment_detail.statuses.canceled'),
                  },
                  {
                    value: 'excluded',
                    label: I18n.t('activerecord.attributes.payment_detail.statuses.excluded'),
                  },
                ]}
                onChangeHandler={e => navigateWithParam('status', e.target.value)}
              />
            </div>
          </div>
          <div className="conditionItem">
            <label className="conditionLabel" htmlFor="filter-start-month">
              {I18n.t('payment_details.start_month')}
            </label>
            <div className="conditionControl">
              <MonthInput
                id="filter-start-month"
                type="month"
                aria-label={I18n.t('payment_details.start_month')}
                defaultValue={f.start_month || ''}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  // 値が未変更ならナビゲーションを発火しない
                  if (e.target.value !== (f.start_month || '')) {
                    navigateWithParam('start_month', e.target.value)
                  }
                }}
              />
            </div>
          </div>
          <div className="conditionItem">
            <label className="conditionLabel" htmlFor="filter-end-month">
              {I18n.t('payment_details.end_month')}
            </label>
            <div className="conditionControl">
              <MonthInput
                id="filter-end-month"
                type="month"
                aria-label={I18n.t('payment_details.end_month')}
                defaultValue={f.end_month || ''}
                onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                  // 値が未変更ならナビゲーションを発火しない
                  if (e.target.value !== (f.end_month || '')) {
                    navigateWithParam('end_month', e.target.value)
                  }
                }}
              />
            </div>
          </div>
          <div className="clearButton">
            <a className="clearLink" href="/agent/payment_details">
              {I18n.t('generic.clear')}
            </a>
          </div>
          <div className="csvButton">
            <a className="csvLink" href={csvExportHref()}>
              {I18n.t('payment_details.csv_export')}
            </a>
          </div>
          {props.sheet_export_enabled && (
            <div className="sheetExportButton">
              <button
                type="button"
                className="csvLink"
                onClick={exportSheet}
                disabled={sheetExportRequested}
              >
                {I18n.t('payment_details.sheet_export')}
              </button>
            </div>
          )}
        </SearchConditions>
        <EditTable
          editable={true}
          columns={COLUMNS}
          records={rows}
          renderRecordControls={renderRecordControls}
          stickyActions={true}
          actionHeader={I18n.t('generic.link')}
        />
        {props.pagination && props.pagination.total_pages > 1 && (
          <Pagination
            onChangePageHandler={navigateToPage}
            currentPage={props.pagination.current_page}
            prevPage={props.pagination.prev_page}
            nextPage={props.pagination.next_page}
            totalCount={props.pagination.total_count}
            totalPages={props.pagination.total_pages}
          />
        )}
      </Panel>
    </SidebarLayout>
  )
}

const SearchConditions = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  gap: 8px 12px;
  flex-wrap: wrap;

  .conditionItem {
    display: flex;
    align-items: center;
  }
  .conditionLabel {
    text-align: right;
    font-size: 12px;
    font-weight: bold;
    margin-right: 6px;
    white-space: nowrap;
  }
  .conditionControl {
    width: 200px;
  }
  .conditionControl.typeahead {
    width: 240px;
  }
  .clearButton {
    margin-left: 4px;
  }
  .clearLink {
    font-size: 12px;
    color: ${constants.COLORS.primary};
    text-decoration: underline;
  }
  .clearLink:hover {
    text-decoration: none;
  }
  .csvButton {
    margin-left: auto;
  }
  .sheetExportButton {
    margin-left: 8px;
  }
  .csvLink {
    display: inline-block;
    margin: 0;
    padding: 8px 16px;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    background: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
    /* a と button でブラウザ既定のフォント・行高が異なるため明示して揃える */
    font-family: inherit;
    font-size: 12px;
    font-weight: bold;
    line-height: 1.5;
    text-decoration: none;
    white-space: nowrap;
  }
  .csvLink:hover {
    opacity: 0.75;
  }
  .csvLink:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const MonthInput = styled.input`
  height: 36px;
  width: 100%;
  padding: 4px 8px;
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 4px;
  font-size: 14px;
`

const GuideCell = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .meta {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  .name {
    color: ${constants.COLORS.primary};
    text-decoration: underline;
    font-size: 13px;
  }
  .name:hover {
    text-decoration: none;
  }
  .rank {
    font-size: 11px;
  }
`

const RecordControls = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  padding: 8px 0;

  .opButton {
    display: inline-block;
    text-align: center;
    padding: 4px 12px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 700;
    text-decoration: none;
    white-space: nowrap;
    transition: opacity 0.2s;
    border: 1px solid transparent;
  }
  .opButton:hover {
    opacity: 0.75;
  }
  .opButton--tour {
    border-color: ${constants.COLORS.primary};
    background: ${constants.COLORS.white};
    color: ${constants.COLORS.primary};
  }
  .opButton--report {
    border-color: ${constants.COLORS.info};
    background: ${constants.COLORS.info};
    color: ${constants.COLORS.white};
  }
  .reportBlock {
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 6px;
  }
`

export default Index
